import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { MapPin, Mail, Clock } from "lucide-react";
import { API, HERO_BG, PageHero, PlanYourVisitForm, TestimonyForm } from "../shared";

const ContactPage = () => {
  const [formType, setFormType] = useState("prayer");
  const [formData, setFormData] = useState({ name: "", email: "", phone: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const endpoint = formType === "prayer" ? "/prayer-request" : formType === "volunteer" ? "/volunteer" : "/connect";
      await axios.post(`${API}${endpoint}`, { ...formData, request: formData.message, interests: formData.message, card_type: formType });
      toast.success("Thank you! We'll be in touch soon.");
      setFormData({ name: "", email: "", phone: "", message: "" });
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="pt-20 fade-in">
      <PageHero eyebrow="Reach Out" title="Contact" italic bg={HERO_BG.contact} testId="contact-title" />

      <section id="plan-your-visit" className="py-24 bg-neutral-50 border-b border-black/5" data-testid="plan-your-visit-section">
        <div className="max-w-4xl mx-auto px-8">
          <div className="text-center mb-12">
            <p className="heading-caps text-[10px] text-red-700 mb-3 tracking-[0.25em]">First Time at WHCO?</p>
            <h2 className="text-4xl md:text-5xl mb-5" style={{ fontFamily: "Playfair Display, serif" }}>
              Plan <span className="italic">Your Visit</span>
            </h2>
            <p className="text-black/60 max-w-xl mx-auto">
              Tell us a little about who&apos;s coming and we&apos;ll have everything ready for you — including childcare, a parking spot, and a friendly face at the door.
            </p>
          </div>
          <div className="bg-white p-10 md:p-14 shadow-sm">
            <PlanYourVisitForm />
          </div>
        </div>
      </section>

      <section className="py-32 bg-white">
        <div className="max-w-7xl mx-auto px-8">
          <div className="grid lg:grid-cols-2 gap-20">
            <div>
              <div className="flex gap-2 mb-12">
                {[{ id: "prayer", label: "Prayer" }, { id: "volunteer", label: "Volunteer" }, { id: "inquiry", label: "Inquiry" }].map((type) => (
                  <button key={type.id} onClick={() => setFormType(type.id)}
                    className={`px-6 py-3 text-xs uppercase tracking-widest transition-all ${formType === type.id ? "bg-black text-white" : "bg-neutral-100 text-black hover:bg-neutral-200"}`}
                    data-testid={`form-type-${type.id}`}
                  >{type.label}</button>
                ))}
              </div>
              <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                <input type="text" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-0 py-4 bg-transparent border-b border-black/20 focus:border-black focus:outline-none" placeholder="Your Name" required data-testid="contact-name"
                />
                <input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-0 py-4 bg-transparent border-b border-black/20 focus:border-black focus:outline-none" placeholder="Email Address" required data-testid="contact-email"
                />
                <input type="tel" value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="w-full px-0 py-4 bg-transparent border-b border-black/20 focus:border-black focus:outline-none" placeholder="Phone (Optional)" data-testid="contact-phone"
                />
                <textarea value={formData.message} onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  rows={4} className="w-full px-0 py-4 bg-transparent border-b border-black/20 focus:border-black focus:outline-none resize-none"
                  placeholder={formType === "prayer" ? "Your prayer request..." : formType === "volunteer" ? "Areas of interest..." : "Your message..."}
                  required data-testid="contact-message"
                />
                <button type="submit" disabled={submitting} className="w-full btn-dark py-5 disabled:opacity-50" data-testid="contact-submit">
                  {submitting ? "Sending..." : "Send Message"}
                </button>
              </form>
            </div>

            <div>
              <div className="bg-black text-white p-12 mb-8">
                <h3 className="text-2xl mb-8" style={{ fontFamily: 'Playfair Display, serif' }}>Visit Us</h3>
                <div className="space-y-6 text-sm">
                  <div className="flex items-start gap-4">
                    <MapPin className="text-white/40 mt-1" size={18} />
                    <div><p>224 W Lancaster Blvd</p><p className="text-white/60">Lancaster, CA 93534</p></div>
                  </div>
                  <div className="flex items-center gap-4">
                    <Mail className="text-white/40" size={18} />
                    <p>info@whco.life</p>
                  </div>
                  <div className="flex items-center gap-4">
                    <Clock className="text-white/40" size={18} />
                    <p>Sat 6:30PM • Wed 7PM</p>
                  </div>
                </div>
              </div>

              <div className="aspect-video">
                <iframe src="https://www.google.com/maps?q=224+W+Lancaster+Blvd,+Lancaster,+CA+93534&output=embed"
                  width="100%" height="100%" style={{ border: 0 }} allowFullScreen="" loading="lazy" referrerPolicy="no-referrer-when-downgrade" title="Church Location"
                ></iframe>
              </div>
            </div>
          </div>
        </div>
      </section>

      <TestimonyForm variant="light" />
    </div>
  );
};

export default ContactPage;
